<template>
  <div id="manning-vessel-list" class="h-full">
    <!-- 右上アクションボタン領域 -->
    <div class="flex flex-wrap justify-between items-center">
      <div class="flex w-1/3 justify-start pl-2">
        <div class="aisea-text-18-600-dark-gray">船舶一覧</div>
      </div>
      <div class="flex w-2/3 justify-end items-center">
        <div
          ref="searchQueryBox"
          class="flex justify-end items-center mr-2"
          style="height: 3.2rem;"
        >
          <vs-tooltip text="検索" position="bottom">
            <div
              class="flex w-full justify-end items-center"
              style="position: relative;"
              @click="changeSearchQueryBoxWidth()"
            >
              <vs-input
                ref="incrementalSearch"
                icon="search"
                :placeholder="incrementalSearchPlaceholder"
                v-model="searchQueryString"
                @input="onSearchQueryUpdate"
                class="manning-vessel-incremental-search"
                :color="AISEA_COLOR.GRAY"
              />
              <vs-icon
                icon="clear"
                :color="AISEA_COLOR.GRAY"
                size="20px"
                style="position: absolute; right: 0.4rem;"
                :style="{ display: searchCondition.name ? 'block' : 'none' }"
                ref="incrementalSearchClear"
                @click="clearSearchQueryString"
              />
            </div>
          </vs-tooltip>
        </div>

        <vs-button
          icon="add"
          :color="AISEA_COLOR.BLUE"
          type="border"
          style="margin: 0 0.6rem; height: 2.7rem;"
          @click="showAddPrompt = true"
          ><span class="aisea-text-14-400-blue">新規登録</span></vs-button
        >
      </div>
    </div>
    <vs-divider :color="AISEA_COLOR.LIGHT_GRAY" class="mt-2 mb-0" />

    <!-- AgGrid Table -->
    <ag-grid-vue
      :gridOptions="gridOptions"
      @sortChanged="onSort"
      class="ag-theme-material w-full ag-grid-table"
      :columnDefs="columnDefs"
      :defaultColDef="defaultColDef"
      :rowData="tableData"
      domLayout="autoHeight"
      rowSelection="multiple"
      colResizeDefault="shift"
      :animateRows="true"
      :floatingFilter="false"
      :suppressMovableColumns="true"
      :suppressPaginationPanel="true"
      :suppressDragLeaveHidesColumns="true"
      :suppressCellFocus="true"
      :suppressRowClickSelection="true"
      :suppressNoRowsOverlay="false"
    />

    <!-- ページネーション領域 -->
    <crew-pagination
      :currentPage="currentPage"
      :pageSize="pageSize"
      :totalElement="totalElement"
      @onChangePageSize="onChangePageSize"
      @onChangePage="onChangePage"
    ></crew-pagination>

    <vs-prompt
      title="船舶情報追加"
      :color="AISEA_COLOR.BLUE"
      :active.sync="showAddPrompt"
      buttons-hidden
      class="manning-vessel-list-add-confirm"
    >
      <vessel-edit
        :inputParam="{ id: null, continueFlg: false }"
        :countryCodeList="countryCodeList"
        :manningVesselCategoryList="vesselCategorieList"
        @close="showAddPrompt = false"
        @reloadList="fetchTableData"
        @updatedId="updatedIdList.push($event)"
      />
    </vs-prompt>

    <vs-prompt
      title="船舶情報編集"
      :color="AISEA_COLOR.BLUE"
      :active.sync="showEditPrompt"
      buttons-hidden
      class="manning-vessel-list-edit-confirm"
    >
      <vessel-edit
        :inputParam="targetRowData"
        :countryCodeList="countryCodeList"
        :manningVesselCategoryList="vesselCategorieList"
        @close="showEditPrompt = false"
        @reloadList="fetchTableData"
        @updatedId="updatedIdList.push($event)"
      />
    </vs-prompt>

    <vs-prompt
      title="船舶情報削除"
      type="confirm"
      :color="AISEA_COLOR.BLUE"
      accept-text="実行"
      cancel-text="キャンセル"
      :active.sync="showDeletePrompt"
      @accept="onDelete"
      @cancel="onDeleteCancel"
      class="manning-vessel-list-delete-confirm"
    >
      <div>
        {{
          targetRowData ? targetRowData.name : ""
        }}の船舶情報を削除しますか？<br />
        削除したデータは戻せません。
      </div>
    </vs-prompt>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import Vue from "vue";
import CellRendererVesselName from "./cell-renderer/CellRendererVesselName.vue";
import CellRendererRowActionButtons from "./cell-renderer/CellRendererRowActionButtons.vue";
import CrewPagination from "../components/CrewPagination.vue";
import VesselEdit from "../vessels-list/components/ManningVesselEdit.vue";
import { AiseaApiFactory } from "@/api/aisea/apiFactory";
const enterpriseManningVesselApi = AiseaApiFactory.get(
  "enterpriseManningVessel"
);
const enterpriseManningVesselCategoryApi = AiseaApiFactory.get(
  "enterpriseManningVesselCategory"
);
const constantApi = AiseaApiFactory.get("constant");
import { DeviceInfoMixin, ColorMixin } from "@/mixins";

export default {
  components: {
    AgGridVue,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererRowActionButtons,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererVesselName,
    CrewPagination,
    VesselEdit,
  },
  mixins: [DeviceInfoMixin, ColorMixin],
  props: {
    activeSidebarCrew: Boolean,
    activeTab: Number,
  },
  data() {
    return {
      tableData: [],

      vesselCategorieList: [],
      countryCodeList: [],

      targetRowData: null,

      updatedRowItemIds: [],

      searchQueryString: "",
      updatedIdList: [],
      showAddPrompt: false,
      showEditPrompt: false,
      showDeletePrompt: false,

      sortType: null,
      sortColumnKey: null,

      incrementalSearchPlaceholder: "",

      pageable: {
        page: 0,
        total_elements: 0,
        total_pages: 1,
      },

      searchCondition: {
        name: "",
        active_type: "ACTIVE",
        sort_column: null,
        sort_order: null,
        page: 0,
        size: 50,
      },

      currentPage: 1,
      gridApi: null,
      gridOptions: {
        localeText: {
          noRowsToShow:
            '<div id="no-rows-manning-vessel-list" class="aisea-text-16-400-black">表示する船舶情報がありません</div>',
        },
        context: {
          componentParent: this,
        },
      },
      pageSize: 50,
      defaultColDef: {
        sortable: true,
        resizable: true,
        suppressMenu: true,
        icons: {
          sortAscending:
            '<span class="material-icons">keyboard_arrow_up</span>',
          sortDescending:
            '<span class="material-icons">keyboard_arrow_down</span>',
          sortUnSort: '<span class="material-icons">unfold_more</span>',
        },
        unSortIcon: true,
        comparator: () => {
          return;
        },
      },
      columnDefs: [],
      prevRoute: null,
    };
  },
  async created() {
    this.initializeColumnDef();
    await this.fetchAllManningVesselCategories();
    await this.fetchAllCountryCodes();
    await this.fetchTableData();
  },
  mounted() {
    let routerViewElement = document.getElementsByClassName("router-view");
    routerViewElement[0].style.padding = "2.2rem";

    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
  },
  watch: {
    currentPage() {
      this.fetchTableData();
    },
  },
  computed: {
    totalPages() {
      if (this.pageable) return this.pageable.total_pages;
      else return 0;
    },

    totalElement() {
      if (this.pageable) return this.pageable.total_elements;
      else return 0;
    },
    sortColumnList() {
      return this.columnDefs.map(x => x.field.toUpperCase());
    },
  },
  methods: {
    initializeColumnDef() {
      const initialColumnDefs = [
        {
          headerName: "船名",
          field: "name",
          minWidth: 240,
          flex: 1,
          cellRenderer: "CellRendererVesselName",
          cellStyle: params => this.customizeCellStyle(params),
        },
        {
          headerName: "船籍",
          field: "country_name",
          minWidth: 150,
          flex: 1,
          cellStyle: params => this.customizeCellStyle(params),
        },
        {
          headerName: "船種",
          field: "vessel_category",
          minWidth: 150,
          flex: 1,
          cellStyle: params => this.customizeCellStyle(params),
        },
        {
          headerName: "総トン数",
          field: "gross_tonnage",
          minWidth: 120,
          flex: 1,
          cellStyle: params => this.customizeCellStyle(params),
        },
        {
          headerName: "船舶コード",
          field: "origin_code",
          minWidth: 120,
          flex: 1,
          cellStyle: params => this.customizeCellStyle(params),
        },
        {
          headerName: "操作",
          field: "action",
          minWidth: 70,
          maxWidth: 70,
          cellRendererFramework: "CellRendererRowActionButtons",
          pinned: "right",
          resizable: false,
          sortable: false,
          cellRendererParams: {
            edit: data => {
              this.targetRowData = data;
              this.showEditPrompt = true;
            },
            delete: data => {
              this.targetRowData = data;
              this.showDeletePrompt = true;
            },
          },
          cellStyle: params => this.customizeCellStyle(params),
        },
      ];
      this.columnDefs = initialColumnDefs;
    },
    onFilterChanged() {
      this.currentPage = 1;
      this.fetchTableData();
    },
    getFetchTableDataSearchQuery() {
      let query = {};
      if (this.searchQueryString) {
        query.name = this.searchQueryString;
      }

      query.size = this.pageSize;
      query.page = this.getCurrentPage();
      if (this.sortType && this.sortColumnKey) {
        query.sort_order = this.sortType;
        query.sort_column = this.sortColumnKey;
      }
      return query;
    },
    getCountryName(countryCode) {
      const countryCodeObj = this.countryCodeList.find(
        x => x.id === countryCode
      );
      return countryCodeObj ? countryCodeObj.name_localized : null;
    },
    async fetchTableData() {
      this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
      try {
        const query = this.getFetchTableDataSearchQuery();
        const res = await enterpriseManningVesselApi.search(query);
        this.tableData = res.data.manning_vessels.map(vessel => {
          return {
            id: vessel.id,
            picture: vessel.picture,
            name: vessel.name,
            country_name: vessel.country_code
              ? this.getCountryName(vessel.country_code.id)
              : null,
            vessel_category: vessel.manning_vessel_category
              ? this.$t(
                  "Views.Crew." + vessel.manning_vessel_category.localized_key
                )
              : null,
            manning_vessel_category: vessel.manning_vessel_category,
            country_code: vessel.country_code,
            gross_tonnage: vessel.gross_tonnage,
            origin_code: vessel.origin_code,
          };
        });

        Object.keys(res.data.pageable).forEach(key => {
          Vue.set(this.pageable, key, res.data.pageable[key]);
        });
        this.$vs.loading.close();
      } catch (e) {
        this.$vs.loading.close();
      }
      this.$emit("checkAlert");
    },
    onChangePage(value) {
      this.currentPage = value;
    },
    async fetchAllManningVesselCategories() {
      try {
        const res = await enterpriseManningVesselCategoryApi.search();
        this.vesselCategorieList = res.data.manning_vessel_categories;
      } catch (e) {
        console.log(e);
      }
    },

    async fetchAllCountryCodes() {
      try {
        const res = await constantApi.fetchCountryCode();
        this.countryCodeList = res.data.country_codes;
      } catch (e) {
        console.log(e);
      }
    },

    onSearchQueryUpdate() {
      this.currentPage = 1;
      this.fetchTableData();
    },

    setPageSize(size) {
      this.pageSize = size;
      this.searchCondition.size = size;
    },

    getCurrentPage() {
      return this.currentPage - 1;
    },
    async onDelete() {
      this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
      const targetData = this.targetRowData;
      try {
        await enterpriseManningVesselApi.delete(targetData.id);
        console.debug("### 削除API", targetData.id);

        this.$vs.notify({
          title: `船舶情報設定`,
          text: `${targetData.name}を削除しました。`,
          color: "success",
          iconPack: "feather",
          icon: "icon-check",
        });
      } catch (e) {
        this.$vs.notify({
          title: `船舶情報設定`,
          text: `${targetData.name}を削除できませんでした。`,
          color: "error",
          iconPack: "feather",
          icon: "icon-check",
        });
      } finally {
        this.$vs.loading.close();
      }
      this.$nextTick(() => {
        this.showDeletePrompt = false;
        this.fetchTableData();
      });
    },
    onDeleteCancel() {
      this.showDeletePrompt = false;
    },

    customizeCellStyle(params) {
      if (this.updatedIdList.includes(params.data.id)) {
        return { backgroundColor: "lightyellow" };
      } else {
        return null;
      }
    },

    onChangePageSize(size) {
      this.gridApi.paginationSetPageSize(size);
      this.setPageSize(size);
      this.currentPage = 1;
      this.$nextTick(() => {
        this.fetchTableData();
      });
    },

    changeSearchQueryBoxWidth() {
      let inputEl = this.$refs.incrementalSearch.$el;
      if (inputEl) {
        inputEl.style.border = `1px solid ${this.AISEA_COLOR.BLUE}`;
        inputEl.style.width = "12rem";
        this.incrementalSearchPlaceholder = "船名で検索";
      }
    },

    clearSearchQueryString() {
      this.searchQueryString = "";
    },

    onSort(object) {
      let sortColumn = object.columnApi.columnModel.viewportColumns.find(
        element => {
          return element.sort != null;
        }
      );

      this.sortType = sortColumn ? sortColumn.sort.toUpperCase() : null;
      let sortColumnKeyLower = sortColumn ? sortColumn.colId : null;
      this.sortColumnKey = sortColumnKeyLower
        ? sortColumnKeyLower.toUpperCase()
        : null;
      this.currentPage = 1;

      this.fetchTableData();
    },
  },
};
</script>

<style lang="scss">
@import "src/assets/scss/aisea/variables";

#manning-vessel-list {
  .vs-avatar--con-img img {
    object-fit: cover;
  }
  .ag-grid-table {
    height: 100%;

    font-family: $aisea-font-family;
    font-size: 16px;
    font-weight: 400;

    .ag-header-cell-text {
      font-family: $aisea-font-family;
      font-size: 12px;
      font-weight: 600;
    }
  }
  .ag-header-cell {
    padding-right: 0;
  }

  .manning-vessel-incremental-search {
    border-radius: 6px;
    width: 2rem;
    transition: all 0.2s 0s ease;

    .vs-inputx {
      background-color: $aisea-pure-white;
      border: none !important;
      padding: 10px 0 8px 2rem !important;
      font-family: $aisea-font-family;
      font-size: 16px !important;
      font-weight: 400;
      color: $aisea-dark-gray;
    }
    .vs-input--icon {
      border-right-color: #ffffff00;
      margin-top: 0.2rem;
      font-size: 16px;
    }
    .vs-input--input:focus {
      box-shadow: 0 3px 10px 0 rgba($aisea-blue, 0.15);
    }
    .vs-input--placeholder {
      color: $aisea-light-gray !important;
      padding-left: 1.8rem !important;
      padding-top: 0.8rem !important;
    }
  }
}

.manning-vessel-list-delete-confirm {
  .vs-dialog-header {
    color: $aisea-black !important;
  }
}
</style>
